import { z } from 'zod'
import { ALLOWED_DEVICES } from '../../../../../shared/domain/Device'
import { ALLOWED_BANNER_SUB_TYPE } from '../../../../domain/BannerSubtype'
import { ALLOWED_BANNER_TYPE } from '../../../../domain/BannerType'
import { UrlValidation } from '../../../../../_kernel/Url'
import { ColorValidation } from '../../../../../_kernel/Color'

const BillboardImageSchema = z.object({
  srcSet: z.record(UrlValidation.shape.value),
  alt: z.string(),
})

const BlockImageSchema = z.object({
  srcSet: z.record(UrlValidation.shape.value),
  alt: z.string(),
})

const HeroImageSchema = z.object({
  srcSet: z.record(UrlValidation.shape.value),
  alt: z.string(),
})

// Announcement Banner Content Schema
const AnnouncementBannerContentSchema = z.object({
  title: z.array(z.string()),
  bgColor: ColorValidation.shape.value.nullable(),
  titleColor: ColorValidation.shape.value.nullable(),
})

// Billboard Banner Content Schema
const BillboardBannerContentSchema = z.object({
  title: z.array(z.string()),
  description: z.string(),
  url: UrlValidation.shape.value.nullable(),
  ctaText: z.string().nullable(),
  bgColor: ColorValidation.shape.value.nullable(),
  titleColor: ColorValidation.shape.value.nullable(),
  descriptionColor: ColorValidation.shape.value.nullable(),
  image: BillboardImageSchema.nullable(),
})

// Block Banner Content Schema
const BlockBannerContentSchema = z.object({
  title: z.array(z.string()),
  description: z.string(),
  ctaText: z.string(),
  url: UrlValidation.shape.value,
  image: BlockImageSchema,
})

// Hero Banner Content Schema
const HeroBannerContentSchema = z.object({
  title: z.array(z.string()),
  image: HeroImageSchema,
})

// Banner Schemas with market support
const AnnouncementBannerSchema = z.object({
  type: z.literal('announcement-banner'),
  viewport: z.enum(ALLOWED_DEVICES).optional(),
  content: AnnouncementBannerContentSchema,
})

const BillboardBannerSchema = z.object({
  type: z.literal('billboard-banner'),
  subtype: z.enum(ALLOWED_BANNER_SUB_TYPE),
  viewport: z.enum(ALLOWED_DEVICES).optional(),
  content: BillboardBannerContentSchema,
})

const BlockBannerSchema = z.object({
  type: z.literal('block-banner'),
  viewport: z.enum(ALLOWED_DEVICES).optional(),
  content: BlockBannerContentSchema,
})

const HeroBannerSchema = z.object({
  type: z.literal('hero-banner'),
  viewport: z.enum(ALLOWED_DEVICES).optional(),
  content: HeroBannerContentSchema,
})

// Union of all banner types
export const BannerSchema = z.discriminatedUnion('type', [
  AnnouncementBannerSchema,
  BillboardBannerSchema,
  BlockBannerSchema,
  HeroBannerSchema,
])

export const GETBannerInformationResponse = z.object({
  banners: z.array(BannerSchema),
})

export type GETBannerInformationResponseType = z.infer<typeof GETBannerInformationResponse>

// Individual banner type exports
export type AnnouncementBannerType = z.infer<typeof AnnouncementBannerSchema>
export type BillboardBannerType = z.infer<typeof BillboardBannerSchema>
export type BlockBannerType = z.infer<typeof BlockBannerSchema>
export type HeroBannerType = z.infer<typeof HeroBannerSchema>

export const GETBannerInformationResponseSafe = z.array(
  z.object({
    type: z.enum(ALLOWED_BANNER_TYPE),
    subtype: z.enum(ALLOWED_BANNER_SUB_TYPE).optional(),
    viewport: z.enum(ALLOWED_DEVICES).optional(),
    content: z.record(z.string(), z.any()),
  })
)

export type GETBannerInformationResponseSafeType = z.infer<typeof GETBannerInformationResponseSafe>

export const GETBannerInformationQuery = z.object({
  market: z.string(),
  language: z.string(),
})

export type GETBannerInformationQueryType = z.infer<typeof GETBannerInformationQuery>
